import {
  Callout,
  Code,
  DocPage,
  DocSection,
  K,
  OL,
  P,
  PageFooter,
  PromptCard,
  UL,
} from "./_components";

export function Triggers() {
  return (
    <DocPage
      eyebrow="Demos"
      title="Trigger phrases & natural demos"
      intro={
        <>
          Trigger phrases are how you get Claude / ChatGPT to pick the right
          tool when a real human chats with your project — without coaching the
          human on tool names. They're also how you get one-click "Try this
          prompt" pills for in-person demos. Add 3-5 phrases per tool and your
          demo Just Works.
        </>
      }
    >
      <DocSection id="what" title="What's a trigger phrase?">
        <P>
          A short sentence a real user would <em>say</em> to invoke your tool.
          Not the tool's name; not a function call signature — actual user
          language.
        </P>
        <Code>{`✅ "cancel order A-100"
✅ "refund the customer named Alice"
✅ "show me this week's pipeline"
✅ "what's the weather in Boston?"

❌ "cancel-order"            (the tool name)
❌ "call cancelOrder with id" (chat-as-API leak)
❌ "do the thing"             (no signal)`}</Code>
        <P>
          When you save trigger phrases on a tool, two things happen behind the
          scenes:
        </P>
        <UL>
          <li>
            They're appended to the tool's served MCP description as{" "}
            <K>"Users typically say things like: …"</K>. The chat-host's model
            uses this when picking which tool to call — so phrases at a
            paraphrase distance from the user's input still trigger the tool.
          </li>
          <li>
            Each phrase becomes a one-click pill on the tool's Triggers tab.
            Click → copied to clipboard → paste into a chat. Useful when you're
            doing a live demo and need a working prompt at the ready.
          </li>
        </UL>
      </DocSection>

      <DocSection id="how" title="How to add them">
        <P>From the SPA:</P>
        <OL>
          <li>Open the project → click a tool in the sidebar.</li>
          <li>
            Click the <K>Triggers</K> tab in the header.
          </li>
          <li>
            Type one phrase per line in the textarea. 1-10 phrases, max 240
            characters each.
          </li>
          <li>
            Hit <K>Save phrases</K>. The pills appear immediately.
          </li>
        </OL>
        <P>Or set them at build time from a chat prompt:</P>
        <PromptCard
          title="Set trigger phrases on an existing tool"
          prompt={`Use project set_trigger_phrases on my "Orders" project, tool "cancel-order". triggerPhrases: ["cancel order A-100", "stop the Alice order", "void this transaction", "refund and close"].`}
        />
        <PromptCard
          title="Set them at creation time (add_data_tool / add_tool)"
          prompt={`Use project add_data_tool on "Sales" for tool "lookup-account" with description "Look up a customer account by name or email", inputSchema { type:"object", properties:{ q:{type:"string"} }, required:["q"] }, annotations { readOnlyHint: true }, data { ok: true }, and triggerPhrases ["find the Acme account", "what's the latest on customer X", "look up acme corp"].`}
        />
      </DocSection>

      <DocSection id="writing" title="How to write good ones">
        <P>This is the only judgment call. A few rules of thumb:</P>
        <UL>
          <li>
            <strong>Write what a non-engineer would actually say.</strong> Read
            your phrase out loud. If it sounds like an API call, rewrite it.
          </li>
          <li>
            <strong>Cover 3-5 paraphrases.</strong> Not 1, not 20. Three or four
            covers the natural variation a human would use.
          </li>
          <li>
            <strong>Include a concrete example value</strong> when the tool
            takes input. <K>"cancel order A-100"</K> is better than{" "}
            <K>"cancel an order"</K> — the example primes the LLM to fill in
            the arg.
          </li>
          <li>
            <strong>Don't list synonyms of the same idea</strong> unless they
            really differ. "Cancel the order" / "void the order" / "kill the
            order" are one phrase, not three.
          </li>
          <li>
            <strong>Include at least one phrase that doesn't mention the
            domain noun.</strong> "stop the Alice order" picks up users who
            describe by customer, not by order ID.
          </li>
        </UL>
      </DocSection>

      <DocSection id="demo" title="Running a demo with trigger phrases">
        <P>The full demo workflow:</P>
        <OL>
          <li>
            Share your project's MCP URL with your audience — e.g.{" "}
            <K>mcpbuild.app/mcp/orders</K>. They connect it in their own Claude
            or ChatGPT.
          </li>
          <li>
            (Or, if they're not on a chat client, send them the public share
            link from your project page — it renders the widgets in a webpage.)
          </li>
          <li>
            On your laptop, open the project page → Triggers tab on each tool.
            The phrase pills are right there.
          </li>
          <li>
            When you want to show a tool, click a pill — copies the prompt to
            your clipboard. Paste into your audience-side chat.
          </li>
          <li>
            Or just <em>say</em> the phrase yourself if you've connected the
            same project endpoint locally — the LLM routes it to the tool, the
            widget renders inline.
          </li>
        </OL>
        <Callout kind="tip" title="The “no-coaching” test.">
          Hand the URL to someone who's never used your project. Don't tell
          them tool names. If they can naturally describe what they want and
          your tool fires — your phrases are working. If they have to guess —
          add more phrases that mirror how they actually spoke.
        </Callout>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/data-tools", label: "Data tools + callTool()" }}
        next={{ to: "/docs/live-data", label: "Live data, secrets & fetchData" }}
      />
    </DocPage>
  );
}
